import parseISO from 'date-fns/parseISO'
// eslint-disable-next-line
import _ from 'lodash'
import {
  ArrayContext, ObjectContext, CoerceScalar, InitAppState, IsObjectVar,
  MODE_REPLACE, MODE_INIT, MODE_OVERLAY, MODE_OVERLAY_CHILD,
} from './context'
import { ArrayState } from '.'
import { objectId } from '..'
import { ANSWERED } from '../../config'
import { GLOBALID, GLOBALTYPE, TOPID } from '../runtimeConstants'
import { ParseQueryExpr } from '../parseQueryExpr'
import { diff } from './diff'
import { unwrap } from './unwrap'
import { extractFields, filterObjectFields, serializeFieldStr } from './fieldutils.mjs'
import { FetchItem, SaveItem } from './querier'
import { addTopLevelShimsToAppDef } from './listappshims'
import debug from '../debug'
import { startTimer, endTimer, formatTimer } from '../timers'

const USERDATA = TOPID
const GLOBALS = GLOBALID
const { Engine, Scope, compileText } = require('yatte')
const { compileExpr } = Engine

/**
 * An instance of ObjectState will exist for every data object created or used by an app at runtime.
 * It wraps and holds a YObject but also manages mutations to the data over time, as well as
 * evaluation and memoization of the object's virtuals.
 * It replaces both RealizeObject AND RuntimeInfo with one unified class for managing the data on the client.
 * It is intended to be the primary means by which state is managed by an app.
 * An app's state is basically an instance of this class.
 */
export class ObjectState extends ObjectContext {
  constructor (name, typeName = '', parentContext = undefined, typeDict = undefined, sourceObj = undefined) {
    if (parentContext) {
      if (!(parentContext instanceof ObjectState || parentContext instanceof ArrayState)) {
        throw new Error('parentContext must be an instance of ObjectState or ArrayState')
      }
    }
    super(name, typeName, parentContext, typeDict)
    let mutable = parentContext ? parentContext.mutable : false
    if (name === USERDATA) {
      mutable = true
    } else if (mutable) {
      const varDef = this.getThisVarDef()
      if (varDef.type === 'selection') {
        mutable = false
        if (varDef.cacheOpts) {
          const queryDef = this.getGlobalDef(varDef.options)
          if (queryDef && queryDef.writeBack) {
            mutable = true
          }
        }
      }
    }
    this.mutable = mutable
    this.evals = {} // holds compiled Expressions and Templates for this obj's expressions, templates and formulas
    this.box = {} // holds data values that are not relevant to the current app, but which we want to round-trip later
    if (sourceObj) {
      this.initSource(sourceObj)
    }
  }

  get hasEvals () {
    return Object.keys(this.evals).length
  }

  get hasData () {
    const hasBoxed = (Object.keys(this.box).length > 0)
    if (hasBoxed) return true
    if (!this.scope || !this.scope.value) return false
    const props = Object.keys(this.scope.value)
    if (props.length === 0) return false
    if (props.length === 1 && props[0] === 'id$' && this.keyName === 'id$' && !this.isSelectionContext) return false
    return true
  }

  get isSelectionContext () {
    return this.parentContext.childrenSel && (this.name in this.parentContext.childrenSel)
  }

  get wasSelectedFrom () {
    return this.box._sel
  }

  register (callback) {
    if (typeof callback === 'function') {
      this.notify = callback
    } else {
      throw new Error('Invalid callback registered')
    }
  }

  /**
   * Similar to getChildContext (in ObjectContext), but this only works for objects from selection variables, to
   * give you a valid object context you can use to view or edit it (sort of) in the UI.
   * @param {string} varName
   * @param {*} sourceObj
   */
  getSelectionContext (varName, sourceObj = null, mode = MODE_INIT) {
    let childState
    const varDef = (this.typeDef.variables || this.typeDef.properties).find(def => def.name === varName)
    if (varDef && varDef.type === 'selection') {
      if (!this.childrenSel) {
        this.childrenSel = {}
      }
      childState = this.childrenSel[varName]
      if (childState && sourceObj) {
        if (sourceObj === -1) { // explicitly delete
          delete this.childrenSel[varName]
          childState = undefined
        } else {
          // this gets called DURING mutation (when adding a list item), not just top-down
          if (mode !== MODE_OVERLAY_CHILD && mode !== MODE_OVERLAY) {
            childState.refreshScope(sourceObj) // this should only be called top-down, not during mutation
          }
        }
      }
      if (!childState && sourceObj !== -1) { // create a new object context for it
        childState = varDef.isList
          ? this._createArrayState(varName, varDef.typeName, varDef.optionKeyName, false)
          : this._createObjectState(varName, varDef.typeName, false)
        if (sourceObj && typeof sourceObj !== 'object') {
          // the selection variable is referring to an object that apparently does not exist or is not available,
          // so... punt?
          sourceObj = null
        }
        childState.initSource(sourceObj, MODE_INIT)
        childState.initVirtuals()
        childState.initScope()
        if (sourceObj) {
          childState.initSelections()
        }
        this.childrenSel[varName] = childState
      }
    }
    return childState
  }

  _createObjectState (varName, typeName, evalOnly = false) {
    return evalOnly
      ? new ObjectContext(varName, typeName, this)
      : new ObjectState(varName, typeName, this)
  }

  _createArrayState (varName, typeName, keyName, evalOnly = false) {
    return evalOnly
      ? new ArrayContext(varName, typeName, keyName, this)
      : new ArrayState(varName, typeName, keyName, this)
  }

  resetSource () {
    // recurse to all children
    const children = Object.values(this.children)
    if (this.childrenSel) {
      children.push(...Object.values(this.childrenSel))
    }
    children.forEach(child => child.resetSource())

    // then reset source for this object
    this.source = undefined
  }

  /**
   * Ensures all the "virtuals" in this state object (compiled templates and expressions) have been compiled
   * and dependency tracking/value change tracking is initialized for this state object.  Should be invoked
   * AFTER initSource, but BEFORE initScope.  Needs only ever be invoked once on a given object state.
   *
   * Performs (or is eventually intended to perform) necessary setup to ensure that
   * optimized evaluation of expressions and templates is possible (i.e., memoization).
   *
   * Note: may cause additional (empty) state objects to be created, as necessary for proper dependency tracking.
   */
  initVirtuals () {
    super.initVirtuals()
    if (!this.hasEvals) {
      let genericId = this.instanceId.replace(/\.\d+/g, '[]')
      if (genericId.startsWith('_._top_var[]')) {
        genericId = '_' + genericId.substring(12)
      }
      if (genericId !== GLOBALS && this.typeDef.type !== 'table') {
        // console.time(this.instanceId)
        // debug.log(`InstanceId ${this.instanceId} variables (${this.typeDef.variables.length})...`)
        this.typeDef.variables.forEach(v => {
          const uses = v.uses[genericId] || 'false'
          const tests = v.tests[genericId] || 'false'
          const relevance = (tests === 'false') ? uses : ['(', tests, ')||(', uses, ')'].join('')
          this._compileExpression(getVirtName(v.name, 'required'), uses, genericId)
          this._compileExpression(getVirtName(v.name, 'relevant'), relevance, genericId)
          // this._compileExpression(getVirtName(v.name, 'default'), v.default, genericId)
          this._compileTemplate(getVirtName(v.name, 'label'), v.label, genericId)
          this._compileTemplate(getVirtName(v.name, 'help'), v.helpText, genericId)
          if (v.type === 'number') {
            // this._compileExpression(getVirtName(v.name, 'minimum'), v.minimum, genericId)
            // this._compileExpression(getVirtName(v.name, 'maximum'), v.maximum, genericId)
          } else if (v.type === 'selection') {
            this._compileExpression(getVirtName(v.name, 'options'), v.options, genericId)
          }
          // if (v.isList) {
          //   this._compileExpression(getVirtName(v.name, 'mincount'), v.mincount, genericId)
          //   this._compileExpression(getVirtName(v.name, 'maxcount'), v.maxcount, genericId)
          // }
        })
        // debug.log(`InstanceId ${this.instanceId} summary template...`)
        this._compileTemplate('.summary', this.typeDef.summary || '', genericId)
        // templates on the type
        // debug.log(`InstanceId ${this.instanceId} templates (${this.typeDef.templates.length})...`)
        this.typeDef.templates.filter(t => t.text && t.text !== '""').forEach(t => {
          this._compileTemplate(t.name, t.text, genericId)
        })
        // formulas on the type
        // debug.log(`InstanceId ${this.instanceId} formulas (${this.typeDef.formulas.length})...`)
        this.typeDef.formulas.forEach(f => {
          this._compileExpression(f.name, getFormulaExpr(f), genericId)
        })
        // console.timeEnd(this.instanceId)
      }
    }
    // also ensure the object has the correct prototype (and a unique id$ if it doesn't already!)
    // existing objects (in lists) loaded during initialization need to be tweaked to point at correct prototype
    // (existing objects SHOULD always have an id$, but may not due to bugs)
    const keyName = this.keyName
    if (keyName && this.typeDef.type !== 'table' && !this.typeDef.name.startsWith('_')) { // was: (keyName === 'id$' || keyName === 'id'), which was too restrictive!
      if (this.source) {
        const prototype = this.hasVirtualDefs ? this.virtualDefs : Object.prototype
        if (Object.getPrototypeOf(this.source) !== prototype) {
          this.source = Object.assign(Object.create(prototype), this.source)
        }
        if (!('id$' in this.source)) {
          // assign an id$ -- look for existing key on which to base it
          if (keyName === 'id$') {
            this.source.id$ = objectId()
          } else {
            this.source.id$ = String(this.source[keyName])
          }
        }
      }
    }
    // recurse to all children
    const children = Object.values(this.children)
    if (this.childrenSel) {
      children.push(...Object.values(this.childrenSel))
    }
    children.forEach(child => {
      child.initVirtuals()
      // initialization of child virtuals can cause changes in source
      if (this.source) {
        this.source[child.name] = child.source
      }
    })
  }

  /**
   * Applies a potentially new sourceObj to this context's scope. A scope is treated immutably,
   * so if the scope's value changes, a new scope will be created rather than modifying the existing one.
   * It is assumed that sourceObj already fits the type -- values are not coerced into expected types.
   * Recursive.
   *
   * THIS FUNCTION IS MEANT TO BE CALLED WHILE RECURSING TOP-TO-BOTTOM; SPECIFICALLY, IT IS CALLED **AFTER**
   * MUTATEVARDATA BUBBLES UP TO REACH THE TOP OF THE DATA MODEL (but before MutateVarData returns, to be clear).
   * THIS GETS CALLED TO PUSH NEW DATA BACK DOWN THROUGH THE DATA STATE, FROM TOP TO BOTTOM.
   * IF YOU CALL IT *DURING* MUTATEVARDATA (i.e., before it has bubbled up to the top), IT WILL BREAK PROPER
   * UPDATING ON THAT PART OF THE DATA TREE LATER, WHEN REFRESHSCOPE IS CALLED (again) THEN!

   * @param {object} sourceObj
   */
  refreshScope (sourceObj) {
    if (this.source) throw new Error('refreshScope called before prior scope was fully initialized') // debug
    const hadData = this.hasData
    const oldObj = hadData ? this.scope && this.scope.value : undefined
    if (sourceObj && sourceObj.__value) { // unwrap proxy to reach actual value
      sourceObj = sourceObj.__value
    }
    const valueChange = (oldObj !== sourceObj)
    if (valueChange) {
      if (this.parentContext instanceof ArrayState) {
        this.scope = Scope.pushListItem(this.name, this.parentContext.scopeGetter)
      } else { // (this.parentContext instanceof ObjectState)
        this.scope = Scope.pushObject(this._getTypedObject(sourceObj, true),
          this.parentContext && this.parentContext.scopeGetter)
      }
      // recursively refresh child states
      const children = Object.values(this.children)
      if (this.childrenSel) {
        children.push(...Object.values(this.childrenSel))
      }
      children.forEach(child => {
        // get potentially mutated value from child state (which should already have current/correct value
        // from earlier in the overlayData/mutateVarData pipeline) and canonize it in child scope
        const childScope = this.getChildScope(child.name)
        child.refreshScope(childScope && childScope.value)
      })
    }
    if (this.needInitSelections) {
      this.initSelections()
      delete this.needInitSelections
    }
  }

  /**
   * Takes a JS object representing the latest state of user data (as modified by mutateVarData)
   * and updates/refreshes the Object State (recursively) based on whatever changes were made.
   * NOT called during App's render()... rather, called as the last step of mutateVarData,
   * that finalizes the state with mutated user data, just prior to state update that triggers a new render()!
   */
  refreshUserData (newValue) {
    if (this.name !== GLOBALS) throw new Error('refreshUserData can only be called on the top-level state object')
    const childState = this.children[USERDATA]
    if (!childState) throw new Error('user data not initialized')
    if (newValue) {
      delete this.scope.items[USERDATA]
      this.scope.value[USERDATA] = newValue
      childState.refreshScope(newValue)
    }
    // TODO: before returning, should we ALSO be calling childState.initSelections() or equivalent???
    // Or somehow only for modified values? Update: see "needInitSelections". Is this enough?
  }

  static clearCaches () {
    ObjectContext.clearCaches()
  }

  _fetchSummary () {
    let result = this.getEvaluated('.summary')
    if (typeof result !== 'string' && this.typeDef.summary) { // for example, table types don't have memoization...
      result = super._fetchSummary()
    }
    return result
  }

  _compileExpression (key, expr, instId) {
    // avoid recompilation/re-evaluation
    // eslint-disable-next-line
    let isNamed
    if (this.virtualDefs && (key in this.virtualDefs)) {
      expr = this.virtualDefs[key]
      // eslint-disable-next-line
      isNamed = true
    }
    let compiled
    if (expr) {
      let func
      if (typeof expr === 'function') {
        func = expr
      } else {
        startTimer()
        func = compileExpr(expr)
        const t = endTimer()
        if (t > 0.25) {
          console.log(`Problematic expression: ${instId ? instId + '.' + key : key}, length=${
            expr.length}, time=${formatTimer(t)}`)
        }
      }
      if (func.constant) {
        compiled = func()
      } else {
        compiled = func
        // experimental: set the function name to help identify it on the stack when debugging
        // Object.defineProperty(compiled, 'name', { value: `E<${key}>` })
        // (commented out because it's not tested super well, plus this only covers a minority of the issue)
        // (more complete solution needed as part of yatte or yatte rewrite!)
      }
    } else {
      compiled = expr
    }
    this.evals[key] = compiled
  }

  _compileTemplate (key, template, instId) {
    // avoid recompilation/re-evaluation
    // eslint-disable-next-line
    let isNamed
    if (this.virtualDefs && (key in this.virtualDefs)) {
      template = this.virtualDefs[key]
      // eslint-disable-next-line
      isNamed = true
    }
    let compiled
    if (template) {
      let func
      if (typeof template === 'function') {
        func = template
      } else {
        startTimer()
        func = compileText(template)
        const t = endTimer()
        if (t > 0.25) {
          console.log(`Problematic template: ${instId ? instId + '.' + key : key}, length=${
            template.length}, time=${formatTimer(t)}`)
        }
      }
      if (func.constant) {
        compiled = func()
      } else {
        compiled = func
        // experimental: set the function name to help identify it on the stack when debugging
        // Object.defineProperty(compiled, 'name', { value: `T<${key}>` })
        // (commented out because it's not tested super well, plus this only covers a minority of the issue)
        // (more complete solution needed as part of yatte or yatte rewrite!)
      }
    } else {
      compiled = ''
    }
    this.evals[key] = compiled
  }

  /**
   * @param {string} name the name of a variable in this context's data model
   * @returns the variable definition for the named variable
   */
  getVarDef (name) {
    return this.typeDef.variables.find(v => v.name === name)
  }

  /**
   * @returns a (possibly synthetic) variable definition for an object or selection variable
   *          representing the current context
   */
  getThisVarDef () {
    const context = this.parentContext
    if (!context) { // this must be global context -- fabricate a dummy variable definition
      return { name: GLOBALID, type: 'object', typeName: GLOBALTYPE, link: this.linkedQuery }
    }
    if (this.name === USERDATA) { // top-level app link -- fabricate a dummy variable definition
      return { name: USERDATA, type: 'object', typeName: this.typeDef.name, link: context.linkedQuery }
    }
    // else look up variable definition from the appropriate type definition
    return context.isList
      ? context.parentContext.getVarDef(context.name)
      : context.getVarDef(this.name)
  }

  getGlobalDef (linkExpr) {
    const globalType = this.typeDefs[GLOBALTYPE]
    const {
      queryName,
      mapTo,
      mapBack,
      args,
    } = ParseQueryExpr(linkExpr)
    let def
    if (queryName) {
      def = globalType.properties.find(p => p.name === queryName)
      if (!def) {
        def = globalType.functions.find(f => f.name === queryName)
      }
    }
    if (def && (mapTo || args)) {
      def = {
        ...def,
        mapTo,
        args,
      }
      if (mapBack) {
        def.mapBack = mapBack
      }
    }
    return def
  }

  * relevantVarDefs () {
    for (const varName of this.relevantVars()) {
      yield this.getVarDef(varName)
    }
  }

  // generators for display and answered status
  * relevantVars () {
    for (const varName of this.typeDef.variables.map(v => v.name)) {
      if (varName !== 'id$' && this.getVarRelevance(varName)) {
        yield varName
      }
    }
  }

  // *requiredVars () {
  //   for (const varName of this.typeDef.variables.map(v => v.name)) {
  //     if (varName !== 'id$' && this.getVarRequired(varName)) {
  //       yield varName
  //     }
  //   }
  // }

  getAllVarsFieldObj (typeName = undefined, includeInternal = false) {
    return GetFieldMask(typeName || this.typeDef.name, this.typeDefs, includeInternal)
  }

  getVarNamesAll (typeName = undefined, includeInternal = false) {
    const fields = this.getAllVarsFieldObj(typeName, includeInternal)
    return serializeFieldStr(fields)
  }

  getVarNamesSummary (typeName = undefined) { // for now, always behaves as if includeInternal == true
    const typeDef = typeName ? this.typeDefs[typeName] : this.typeDef
    return typeDef.sumFields
  }

  // answered status calculation
  getStatus () {
    let objectStatus = ANSWERED.FULL
    let foundFullAnswer = false
    let foundPartAnswer = false // allow partially-answered child objects to affect parents' status
    for (const varName of this.relevantVars()) {
      let varStatus = ANSWERED.NONE
      if (this.scope.hasProperty(varName)) {
        const thisAns = this.scope.getProperty(varName, this.scopeGetter)
        if (thisAns !== undefined && thisAns !== null && thisAns !== '') {
          const thisDef = this.typeDef.variables.find(varDef => varDef.name === varName)
          if (thisDef && thisDef.type === 'object') {
            const childState = this.children[varName]
            if (childState) {
              varStatus = childState.getStatus()
            } else {
              throw new Error(`Child state does not exist for relevant object variable '${varName}'`)
            }
          } else {
            varStatus = ANSWERED.FULL
            // const check = thisAns.__value
            // if (check) { // it's an answer to a dynamic selection variable, so we have a proxy object
            //   thisAns = check // get the actual object instead
            // }
          }
        } else {
          varStatus = ANSWERED.NONE
        }
      }
      if (varStatus === ANSWERED.FULL) {
        // possible outcomes -> object is either FULLy or PARTially answered
        foundFullAnswer = true
        // keep looking through vars until we find anything required-but-not-fully-answered
      } else {
        // possible outcomes -> object could have ANY answered status...
        foundPartAnswer = foundPartAnswer || (varStatus === ANSWERED.PART)
        if (this.getVarRequired(varName)) {
          // this var is required BUT it is NOT FULLy answered
          // possible outcomes -> object is either PARTially answered or NONE (not answered)
          objectStatus = ANSWERED.PART // so the object will be AT MOST partially answered, else not answered
          if (foundFullAnswer || foundPartAnswer) {
            break // if any answers have been encountered, we stop looking because we know we're partially answered
          }
          // otherwise we keep looking until we either find some kind of answer or determine everything is unanswered
        }
        // else if var is not required, we have to keep looking to determine final status
      }
    }
    if (foundFullAnswer || foundPartAnswer) {
      return objectStatus // either ANSWERED.FULL or ANSWERED.PART
    } // else
    return ANSWERED.NONE
  }

  // memoized getters:
  getVarRelevance (name) {
    return this.getEvaluated(getVirtName(name, 'relevant'))
  }

  getVarRequired (name) {
    return this.getEvaluated(getVirtName(name, 'required'))
  }

  getVarLabel (name) {
    return this.getEvaluated(getVirtName(name, 'label'))
  }

  getVarHelp (name) {
    return this.getEvaluated(getVirtName(name, 'help'))
  }

  getVarMinimum (name) {
    return this.getEvaluated(getVirtName(name, 'minimum'))
  }

  getVarMaximum (name) {
    return this.getEvaluated(getVirtName(name, 'maximum'))
  }

  getEvaluated (name) {
    const evaluator = this.evals[name]
    if (typeof evaluator === 'function') {
      const scope = this.scope
      let stack, result
      if (scope) {
        stack = scope.__yobj || ((scope instanceof Scope) ? scope : Scope.pushObject(scope))
      }
      try {
        result = stack.evaluate(evaluator)
      } catch (e) {
        if (e.name === 'RecursionError') {
          if (e.frames) {
            e.frames.push(evaluator.lbl || name)
          }
          throw e // need the error to bubble all the way up/out of the recursion!
        } else {
          console.error(e.toString())
        }
      }
      return result
    }
    // else it's not a function
    return evaluator
  }

  initMainContextMutable (sourceObj, recordId, catalogName, links, tenancy) {
    // compare/contrast to method refreshUserData(), which is used to refresh the main context after every mutation
    if (this.name !== GLOBALS) throw new Error('Invalid attempt to initialize main data context')
    // todo: add a non-enumerable property called 'id$' to sourceObj, with value of recordId??
    if (sourceObj) {
      if (this.isGlobalList) {
        // in this case, sourceObj should be an array, and we are initializing this app to edit the list
        // (which is sort of a hack, but that's how we made it work)
        if (Array.isArray(sourceObj)) {
          sourceObj = { _top_var: sourceObj }
        }
      } else {
        // initialize the data record's typed object cache
        sourceObj = this.getDataMgr().extractStaticData(sourceObj)
      }
      // now complete the (recursive) initialization of the data state, which was already
      // partially initialized (empty) back in ObjectState.initializeRuntime
      const childDataState = this.children[USERDATA]
      childDataState.initSource(sourceObj, MODE_INIT)
      childDataState.initVirtuals()
      childDataState.initScope()
      // initUserData ensures the '_' identifier is available for evaluation and points to the initialized record.
      // this must come BEFORE the call to initSelections, because initSelections has to evaluate
      // all the option expressions for various selection variables, and those options expressions
      // may refer to _.  Hopefully the options expressions don't ALSO require that all the selection
      // variables need to have previously been initialized, or we have a race condition!
      this.initUserData(childDataState.scope)
      childDataState.initSelections()
      // call AGAIN now that the selection variables have been initialized, to ensure _ has the final version
      this.initUserData(childDataState.scope)
      // alternatively we could probably just do:
      // const childDataState = this.getChildContext(USERDATA, sourceObj)
      // but I like the explicitness above for debugging purposes
      this.mutateVarData(USERDATA, childDataState.scope.value) // triggers the UI refresh
    }
    this.initRecordProps(recordId, catalogName, links, tenancy)
  }

  getMainContextSavables () {
    const links = this.links && Object.values(this.links) // convert link dictionary to array for server
    const userData = this.getChildContext(USERDATA)
    const _updating = userData._updating
    let values
    if (!_updating) {
      values = userData.cleanObject()
      if (this.isGlobalList && values._top_var) {
        // eliminate dynamically-inserted top-level list variable
        values = values._top_var
        // also, no query cache (static data) for global list variables
      } else {
        this.dataManager.injectStaticData(values)
      }
    }
    return { links, values, _updating, recordId: this.recordId, appKey: this.appKey }
  }

  staticDataRefreshed () {
    if (this.notify) {
      this.notify(undefined, true)
    }
  }

  // generator for recursive enumeration of data points currently linked to external queries
  * queryLinks () {
    // if this context is linked to an external query, return an object describing the link
    const thisOne = this.getQueryLink()
    if (thisOne) yield thisOne
    // then recurse to all child contexts (this.children) and childSelection contexts (this.childrenSel)
    if (!thisOne || thisOne.linkType !== 'select') {
      const children = Object.values(this.children)
      if (this.childrenSel) {
        children.push(...Object.values(this.childrenSel))
      }
      for (const childContext of children) {
        yield * childContext.queryLinks()
      }
    }

    /*
      Also, when enumerating query links... a linked SELECTION object need not enumerate any child objects
      of the selection (if there are any).  A 'regular' linked OBJECT might or might not enumerate child objects.
      The high-level view is:  if the child link came by virtue of the parent, such that saving changes to the parent
      would necessarily/automatically include saving any changes to the child, then the child should probably not
      be enumerated by the parent.  But if the child link was established independently of the parent -- for example,
      the child is a new object that is not (strictly speaking) a "part" of the parent object, at least insofar as the
      query goes -- such that saving the parent object back to the query source would NOT also save the child objects
      -- then we should enumerate the child contexts separately so we know they need to be saved.

      Oof, that sounds messy, especially when dealing with 2-way mapping.

      The top-level object in an app may be linked to a Clio matter, and some object variables (which really probably
      ought to be selection variables?) are automatically also linked to Clio contacts that get populated FROM
      the matter.  These should not be enumerated, because they are set by the parent.  But there may also be
      Contact-type (or contact-linked) variables that do NOT get set automatically by the matter, and changes to these
      would NOT be saved automatically by virtue of saving changes to the matter.  These need to be enumerated.

      Maybe: if the reverse mapping (which takes the top-level model back to a matter) can produce anything that is not
      a constant falsy value for a member, the mapping will "cover" that member, otherwise it won't??
    */
  }

  getQueryLink () {
    // "link" objects could look something like this:
    // {
    //   instanceId: string,
    //   label: string,
    //   linkType: 'link' vs. 'select'
    //   linkExpr: string,
    //   keyVal: string,
    //   diffs: array of json patch objects?  or something that would let you get there from here?,
    // }
    const varDef = this.getThisVarDef()
    if (varDef && IsObjectVar(varDef)) {
      const linkExpr = varDef.type === 'selection' ? varDef.options : varDef.link
      if (linkExpr) {
        let linkType, queryDef, keyName
        if (varDef.type === 'selection') {
          linkType = 'select'
          keyName = varDef.optionKeyName || this.typeDef.key || 'id$'
        } else {
          linkType = 'link'
          queryDef = this.getGlobalDef(linkExpr)
          keyName = queryDef && (this.typeDefs[queryDef.typeName].key || queryDef.keyName)
        }
        const keyVal = this.getQueryLinkKey(linkType, keyName, queryDef && queryDef.mapTo) // queryDef?.mapTo
        if (keyVal) {
          return {
            instanceId: this.instanceId,
            linkType,
            linkExpr,
            keyName,
            keyVal,
          }
        }
      }
    }
  }

  /**
   * The REMOTE object key/id, if any, to which the local object or selection context is linked.
   * If this is an object context, this returns the key value to which this object is linked.
   * If this is a selection context, this returns the key value representing the primitive answer
   * of this instance of the selection variable.
   *
   * Note: the implementation once (experimentally) referred to this.parentContext.linkedKeyName.
   * This did not work, and was removed, because linkedKeyName applies to the REMOTE (linked) object,
   * but not necessarily the local object!  (i.e., linkedKeyName was only applicable in situations
   * with custom keys and no mapping... with default keys, it would break.)
   */
  getQueryLinkKey (linkType, keyName, mapping = undefined) {
    let keyVal = this.linkedKeyVal
    if (!keyVal && this.hasData) {
      const objVal = this.scope.value
      let localName
      if (linkType === 'link') {
        if (mapping || !keyName || keyName === 'id$') {
          // under MOST circumstances, the queried object's key is in _link
          localName = '_link'
        } else {
          // UNLESS: there is NO mapping AND the model or query specifies a custom (non-id$) key
          localName = keyName // in which case, the queried object's key will be in [keyName]
        }
        keyVal = objVal[localName]
      } else { // linkType === 'select'
        // queried object's key is in keyName
        keyVal = objVal.__keyVal || objVal[keyName]
      }
    }
    return keyVal
  }

  getQueryStatus (linkExpr) {
    // linkExpr is name or expr of a query to which objects of appropriate types can be linked
    const queryDef = this.getGlobalDef(linkExpr)
    if (queryDef) {
      const { typeName, keyName, writeBack, mapTo, mapBack } = queryDef
      // first get the "current" value of the link, evaluating the mapping to do so if necessary
      let currentValue, fields
      if (mapBack) {
        // we are "back-mapping"... first evaluate the backMap...
        const evalValue = this.evaluateExpression(mapBack)
        if (evalValue) {
          currentValue = unwrap(evalValue, typeName, this.typeDefs, true)
          // ensure back-mapped object has expected key -- for native Knackly queries!
          // if (!(keyName in currentValue)) { // key swap from id$ to other, if necessary? Will this ever happen? // TODO: comment this out!!!!!
          //   debug.assert('id$' in currentValue, 'Internal error: back-mapped value missing id$ key')
          //   currentValue[keyName] = currentValue.id$
          //   delete currentValue.id$
          // }
          // sanity check the back-mapped object against the original
          if (this.linkedKeyName && this.linkedKeyVal && (this.linkedKeyName in currentValue)) {
            debug.assert(this.linkedKeyVal === currentValue[this.linkedKeyName], 'Internal error: linkedKeyVal mismatch')
          }
          // also take note of which fields were initialized by the mapping
          fields = extractFields(currentValue)
        }
      } else { // no mapping
        currentValue = this.cleanObject()
        fields = this.getAllVarsFieldObj(typeName, queryDef.provider === 'Knackly')
      }
      // figure out the key of that value
      const queryModelDef = this.typeDefs[typeName]
      let objKey
      if (!mapTo || mapBack) { // either no mapping OR 2-way/reversible mapping
        objKey = queryModelDef.key || keyName
        if (currentValue && !objKey) {
          if ('id$' in currentValue) {
            objKey = 'id$'
          }
        }
        if (!mapTo && objKey === 'id$' && ('_link' in currentValue)) { // no mapping, and we have a _link
          // id$ is the key of THIS object, _link is the key of the LINKED object... so use _link!
          objKey = '_link'
        }
      } else { // 1-way mapping -- currentValue based on different model than query's
        objKey = '_link' // rely on _link having remembered original key value
        // const mapDef = queryModelDef.formulas.find(fd => fd.name === mapTo)
        // const valueTypeDef = mapDef && this.typeDefs[mapDef.typeName]
        // objKey = valueTypeDef.key || 'id$'
      }
      let hasVal = false
      if (currentValue) {
        const nonEmptyProps = Object.entries(currentValue)
          .filter(entry => entry[1] !== null && entry[1] !== undefined)
          .map(entry => entry[0])
        if (nonEmptyProps.length > 1) {
          hasVal = true
        }
      }
      // create object to describe status of this context vis-a-vis the currently selected/linked item in the query
      const result = {
        value: currentValue, // a POJO representing the ObjectState's *current* query value (post mapping)
        queryDef,
        entityLabel: typeName, // TODO: something more user-friendly here!
        hasVal,
        hasLink: false,
        canModify: writeBack && (!mapTo || !!mapBack), // if mapping is only 1-way, do not allow save on this object
        hasMods: false,
      }
      // compare current value with cached, previously-selected value
      if (hasVal) {
        if (objKey) {
          const keyVal = currentValue[objKey]
          if (keyVal) { // object is linked
            result.linkValue = keyVal
            let cachedValue = this.getDataMgr().getCachedValue(typeName, keyVal, false, false)
            if (cachedValue) {
              result.hasLink = true
              if (this.linkedKeyVal) { // if prior to query selection or refresh, may not have been set yet!
                debug.assert(this.linkedKeyVal === keyVal, `Internal error: no linkedKeyVal (${this.linkedKeyVal})`)
              }
              if (fields) {
                cachedValue = filterObjectFields(cachedValue, fields)
                currentValue = filterObjectFields(currentValue, fields)
              }
              // This diff function returns an array of JSON Patch objects describing the set of modifications
              // that must be applied to origValue, in order for it to (more or less?!) match value.
              const diffs = diff(cachedValue, currentValue, typeName, this.typeDefs)
              if (diffs && diffs.length > 0) {
                result.hasMods = true
                result.origValue = cachedValue // POJO representing an earlier state of the linked "source" object
                result.diffs = diffs // differences between that earlier state and the current state
              }
            }
            // also store what's necessary for the query to (subsequently) be refreshed
            result.linkInstanceId = this.instanceId
            result.linkExpr = linkExpr
            result.linkType = 'link'
          }
        } else {
          console.error('Unable to determine object key')
        }
      }
      return result
    }
  }

  getQueryLinks (userDataState) {
    if (this.name !== GLOBALS) throw new Error('getQueryLinks can only be called on the top-level state object')
    // enumerate all query links, and sort them into order of top-most in the data model
    let allLinks = Array.from(userDataState.queryLinks())
      .sort((a, b) => a.instanceId.length - b.instanceId.length)
    const queryInstanceIndex = {} // used in detection of child/dependent queryLinks
    const roots = [] // queryLinks NOT dependent on (i.e. child of) another queryLink
    for (let i = 0; i < allLinks.length; i++) {
      const thisLink = allLinks[i]
      const parentLinkIId = Object.keys(queryInstanceIndex).find(iid => thisLink.instanceId.startsWith(iid))
      if (parentLinkIId) {
        const parentLink = queryInstanceIndex[parentLinkIId]
        if (!parentLink.dependents) parentLink.dependents = []
        parentLink.dependents.push(thisLink)
      } else {
        roots.push(thisLink)
      }
      queryInstanceIndex[thisLink.instanceId] = thisLink
    }
    return roots
  }

  async updateQueryLinks () {
    if (this.name !== GLOBALS) throw new Error('updateQueryLinks can only be called on the top-level state object')
    const userDataState = this.getChildContext(USERDATA)
    userDataState._updating = {} // cache for data object promises (during update)
    const roots = this.getQueryLinks(userDataState)
    // now perform the refreshes in a top-to-bottom order, conscious of which fields are needed and which are not
    // (i.e. nested selections that will be populated by their OWN nested refresh), and consious of dependencies
    // so e.g. we finish a higher-level update (a top-level link) before initiating a lower-level update that is
    // dependent on the higher-level one and thus may be querying the wrong ID if performed first.
    // Each "root" queryLink is in charge of refreshing those underneath it:
    await Promise.all(
      roots.map(
        link => userDataState.refreshCachedObject(link.linkExpr, link.keyVal, link.instanceId, link.linkType,
          link.dependents, true)
      )
    )
    // The above call to refreshCachedObject (currently) not only re-fetches items from their queries and updates
    // the cached objects, but ALSO (for each item) updates the data state with the latest linked data.
    // At some point, especially if we want to make all or part of that updating conditional, that stuff may need to
    // be refactored out here.
    delete userDataState._updating // at this point the update is finished so we discard the cache
  }

  updateInProgress () {
    const userDataState = this.global.getChildContext(USERDATA)
    return userDataState && Boolean(userDataState._updating)
  }

  getInstance (instanceId) {
    let currContext = this.global
    const instIdParts = instanceId.split('.')
    for (let currPart of instIdParts) {
      let index, isList = false
      if (currPart.endsWith(']')) {
        isList = true
        const i = currPart.indexOf('[')
        index = currPart.slice(i + 1, -1)
        if (index.length > 0) {
          index = Number.parseInt(index)
        }
        currPart = currPart.slice(0, i)
      }
      if (currContext.elements && currPart in currContext.elements) {
        currContext = currContext.elements[currPart]
      } else if (currContext.children && currPart in currContext.children) {
        currContext = currContext.children[currPart]
      } else if (currContext.childrenSel && (currPart in currContext.childrenSel)) {
        currContext = currContext.childrenSel[currPart]
      } else {
        throw new Error(`Unknown instance '${currPart}' in ${instanceId}`)
      }
      if (isList && typeof index === 'number') {
        currContext = currContext.elements[index]
      }
    }
    return currContext
  }

  async refreshCachedObject (queryExpr, objId, instanceId, linkType, dependentLinks = undefined, isAuto = false
  ) {
    const isManual = !isAuto
    const itemKey = objId + ':' + queryExpr // unique key for dedupe/_updating
    // look up the specific data context for instanceId
    const instIdParts = instanceId.split('.')
    let selName
    if (linkType === 'select') {
      // instanceId for selection variables includes selection variable name -- omit for parent context lookup
      selName = instIdParts.pop()
    }
    let currContext = this.getInstance(instIdParts.join('.'))
    const queryDef = this.getGlobalDef(queryExpr)
    // when refreshing ALL (automatically at beginning of app run), for now, skip Clio & Filevine LINK queries,
    // they have to be refreshed manually.  But auto-refresh everything else: Knackly link queries & all select queries.
    if (isManual || linkType === 'select' || queryDef.provider === 'Knackly') {
      // dedupe query calls / only request the same object one time
      if (!(itemKey in this._updating)) {
        let dnrFields
        if (linkType !== 'select' && dependentLinks) {
          // take dependentLinks into account when fetching data... don't bother requesting *now* the fields of
          // child objects that are covered by dependent query links... we will subsequently refresh them separately
          // IF (and hopefully only if!) they are still relevant after this refresh operation!
          dnrFields = buildLimitFields(instanceId, dependentLinks)
        }
        this._updating[itemKey] = FetchItem(queryDef, objId, queryDef.keyName, currContext, instanceId, false, dnrFields)
      }
      let obj
      try {
        obj = await this._updating[itemKey]
      } catch (e) {
        console.log(e.message)
      }
      // now update the data state
      if (obj) {
        if (linkType === 'select') {
          currContext.mutateVarData(selName, obj)
        } else {
          const keyNameHack = (queryDef.provider === 'Knackly' && queryDef.keyName === '_id') ? 'id$' : queryDef.keyName
          if (queryDef.mapTo) {
            currContext.linkMappedObjectValue(obj, queryDef.typeName, keyNameHack, queryDef.mapTo)
          } else {
            currContext.linkSimpleObjectValue(obj, queryDef.typeName, keyNameHack)
          }
        }
      }
    }
    // after this query link has been refreshed, refresh any other query links that depend on it and/or
    // are lower down in the data hierarchy
    if (dependentLinks && (isManual || queryDef.provider === 'Knackly')) {
      await Promise.all(
        dependentLinks.map(
          link => {
            let origKeyVal = link.keyVal
            // It's possible that refreshing the parent queryLink could have made the former linkKeyVal irrelevant.
            if (link.linkType === 'select') {
              // Check if the key of the dependent query was changed by the refresh
                // NOTE: BUT we also (maybe more importantly?) need to check for selection variables that were NOT SET by the parent queryLink!
                // Because stuff NOT CONTROLLED BY the parent queryLink, needs to be AUTO-refreshed; the rest can be manual!
              const newContext = this.getInstance(link.instanceId)
              const newKeyVal = newContext && newContext.getQueryLinkKey(link.linkType, link.keyName)
              if (newKeyVal !== origKeyVal) {
                console.log(`Child query updated: refreshing ${newKeyVal} instead of ${origKeyVal}`)
                if (newKeyVal) {
                  origKeyVal = newKeyVal
                } else {
                  return null
                }
              }
            }
            return this.refreshCachedObject(link.linkExpr, origKeyVal, link.instanceId, link.linkType,
              link.dependents, isAuto)
          }
        )
      )
    }
  }

  /**
   * Uses mapExpr to transform value into this ObjectState's model; then copies the transformed
   * value into this ObjectState (merging/overlaying the current data, if any) AND establishes a link,
   * which embeds the original object ID in this ObjectState so it can potentially be used in the future
   * to refresh the link.
   *
   * Can be called to establish the link initially OR after a modified object has been successfully written back
   * to a modifiable query on the server, to ensure the local data state stays in sync with the now-modified
   * state on the server.
   *
   * @param {object} value    the object (POJO) containing the desired data
   * @param {string} typeName the name of the model that describes value / of which value is an instance
   * @param {string} keyName  (optional) the name of a property/variable in value that is meant to act as a unique key
   * @param {string} mapExpr  (optional) a mapping expression that transforms value
   *                          into an instance of this context's model
   */
  linkMappedObjectValue (value, typeName, keyName, mapExpr) {
    if (!value) throw new Error('Cannot link undefined or null object')
    if (!mapExpr) throw new Error ('required parameter mapExpr not provided')
    const valueTypeDef = this.typeDefs[typeName]
    let objKey = valueTypeDef.key || keyName
    if (!objKey) {
      if ('id$' in value) {
        objKey = 'id$'
      } else {
        throw new Error(`Unable to determine object key of ${JSON.stringify(value)}`)
      }
    }
    const keyVal = value && value[objKey]
    if (keyVal) {
      this.linkedKeyName = objKey
      this.linkedKeyVal = keyVal
    } else {
      throw new Error(`Cannot link to unkeyed ${typeName}: no ${objKey} in ${JSON.stringify(value)}`)
    }
    // "map" this object to another object of a different type, if requested
    // prepare for evaluation
    const c = this.getPushedContext(value, typeName)
    debug.assert(c.typeDef.name === typeName, 'Internal error: type mismatch')
    // evaluate mapping expression
    const evalValue = c.evaluateExpression(mapExpr)
    // since evalValue is the result of evaluation, it's typically a "proxy" object -- i.e., it can have virtuals --
    // so it needs to be "unwrapped" into a POJO before copying the value
    value = unwrap(evalValue, this.typeDef.name, this.typeDefs, false)
    debug.assert(value._link === keyVal)
    // ALSO: it's possible that evaluating the mapping could have triggered additional async data fetching,
    // if for example the mapping formula referred to additional queries, but
    // 'queries during mapping' is not currently supported!
    const overlayed = this.overlayData(value, true)
    this.parentContext.mutateVarData(this.name, overlayed)
  }

  /**
   * Copies value into this ObjectState (merging/overlaying the current data, if any) and establishes a link,
   * which embeds the original object ID in this ObjectState so it can potentially be used in the future
   * to refresh the link.
   *
   * Can be called to establish the link initially OR after a modified object has been successfully written back
   * to a modifiable query on the server, to ensure the local data state stays in sync with the now-modified
   * state on the server.
   *
   * @param {object} value    the object (POJO) containing the desired data
   * @param {string} typeName the name of the model that describes value / of which value is an instance
   *                          (SHOULD NOT BE NECESSARY -- TODO REMOVE)
   * @param {string} keyName  (optional) the name of a property/variable in value that is meant to act as a unique key
   */
  linkSimpleObjectValue (value, typeName, keyName) {
    if (!value) throw new Error('Cannot link undefined or null object')
    const valueTypeDef = this.typeDef
    // todo: remove typeName parameter, which SHOULD not be needed... for now we just test this assertion:
    debug.assert(typeName === valueTypeDef.name, 'typeName mismatch')
    let objKey = valueTypeDef.key || keyName
    if (!objKey) {
      if ('id$' in value) {
        objKey = 'id$'
      } else {
        throw new Error(`Unable to determine object key of ${JSON.stringify(value)}`)
      }
    }
    const keyVal = value && value[objKey]
    if (keyVal) {
      this.linkedKeyName = objKey
      this.linkedKeyVal = keyVal
    } else {
      throw new Error(`Cannot link to unkeyed ${typeName}: no ${objKey} in ${JSON.stringify(value)}`)
    }
    // overlay this value directly on the current data
    // HOWEVER, we need to ensure current data gets appropriately UNSET, as well SET, by this overlay,
    // so we must check to see if there was another overlay in the past, and compare that previously-overlaid
    // value to the new value, making sure to note explicit removals as undefined values.
    const oldLink = this.getValue('_link')
    if (oldLink) {
      let cachedValue = this.getDataMgr().getCachedValue(typeName, oldLink, false, true)
      if (cachedValue) {
        undefineOldProperties(value, cachedValue)
      }
    }
    const overlayed = this.overlayData(value, true)
    this.parentContext.mutateVarData(this.name, overlayed)
  }

  /**
   * Uses mapExpr to transform value into this ObjectState's model; then copies the transformed
   * value into this ObjectState (merging/overlaying the current data, if any).
   *
   * @param {object} value    the object (POJO) containing the desired data
   * @param {string} typeName the name of the model that describes value / of which value is an instance
   * @param {string} mapExpr  (optional) a mapping expression that transforms value
   *                          into an instance of this context's model
   */
  copyMappedObjectValue (value, typeName, mapExpr) {
    if (!value) throw new Error('Cannot set undefined or null linked object')
    if (!mapExpr) throw new Error ('required parameter mapExpr not provided')
    // "map" this object to another object of a different type, if requested
    // prepare for evaluation
    const c = this.getPushedContext(value, typeName)
    debug.assert(c.typeDef.name === typeName, 'Internal error: type mismatch')
    const keyValue = value[c.keyName] || null
    // evaluate mapping expression
    const evalValue = c.evaluateExpression(mapExpr)
    // since evalValue is the result of evaluation, it's typically a "proxy" object -- i.e., it can have virtuals --
    // so it needs to be "unwrapped" into a POJO before copying the value
    value = unwrap(evalValue, this.typeDef.name, this.typeDefs, false)
    // ALSO: it's possible that evaluating the mapping could have triggered additional async data fetching,
    // if for example the mapping formula referred to additional queries, but
    // 'queries during mapping' is not currently supported!
    this.copySimpleObjectValue(value, keyValue)
  }

  /**
   * Copies value into this ObjectState (merging/overlaying the current data, if any).
   *
   * @param {object}                  value    the object (POJO) containing the desired data
   * @param {string|number|undefined} keyValue (optional) override the unique key value for the given object
   */
  copySimpleObjectValue (value, keyValue = undefined) {
    if (!value) throw new Error('Cannot set undefined or null linked object')
    if ('_link' in value) {
      // if value is linked to something else, we're NOT copying that link... just the value itself!
      const { _link, ...valueCopy } = value
      value = valueCopy
    }
    if (keyValue === undefined) {
      keyValue = value[this.keyName]
    }
    if (['string', 'number'].includes(typeof keyValue)) {
      // remember the unique key of the "selected from" object as a boxed property
      this.box._sel = keyValue
    }
    // overlay the value on the current data
    const overlayed = this.overlayData(value, false, true)
    this.parentContext.mutateVarData(this.name, overlayed)
  }

  overlayData (value, doLink, retainId$ = undefined) {
    // value is an object that matches this.typeDef. It may be coming DIRECTLY from an external source (in which case
    // it's a POJO), or it may have been MAPPED from an external source (in which case it already has prototypes).
    // It would be nice if we could verify the above assumption(s) and unambiguously tell which it was.
    // This function overlays value on top the current scope, with properties of value overwriting those of the
    // current scope, EXCEPT all object keys/ids are retained from the current scope. It returns a new object scope,
    // retaining the current scope's key value and any of its other data NOT overwritten by value.
    if (!value || typeof value !== 'object') throw new Error('Overlay requires an object value')
    const existingLink = this.linkedKeyVal || this.getValue('_link')
    const currentVal = this.scope && this.scope.value
    const newObject = this._getTypedObject(currentVal,
      (doLink && value._link && existingLink === value._link) || retainId$ || false,
      true)
    const typeDef = this.typeDef
    if (doLink && this.linkedKeyName && value._link) {
      debug.assert(this.linkedKeyVal === value._link)
    }
    const varDefs = typeDef.variables || typeDef.properties
    const keyName = typeDef.key || 'id$'
    const dataMgr = this.getDataMgr()
    if (doLink) {
      let linkedKeyValue = value._link
      if (!linkedKeyValue && keyName === 'id$') {
        // object may be result of evaluation/child object mapping, but without an explicit _link...
        const keyValue = value[keyName]
        if (keyValue && dataMgr.hasCachedValue(typeDef.name, keyValue)) { // key originated with query
          linkedKeyValue = keyValue
        }
      }
      if (linkedKeyValue) { // new object needs to remember original key (if it doesn't have it already)
        newObject._link = linkedKeyValue
      }
    }
    for (const [propName, propVal] of Object.entries(value)) {
      if (propName === keyName && propName in newObject) continue // don't retain key prop if we already have one
      if (propName === '_link') continue // we already set the _link prop, if necessary, above
      if (propVal === newObject[propName]) continue // correct value is already set
      let newVal = propVal
      const varDef = varDefs.find(def => def.name === propName)
      if (varDef && propVal) {
        if (varDef.type === 'object') {
          // the code below seems to handle varDef.isList correctly, no special case
          if (typeof propVal === 'object') {
            const childContext = this.getChildContext(propName)
            newVal = childContext.overlayData(propVal, doLink)
          }
        } else if (varDef.type === 'selection') {
          // selected values shouldn't "overlay", per se -- they really should just get switched out.
          // but we DO still need to recurse into them to make sure nested contexts are appropriately defined!
          if (varDef.isList) {
            const arrayVal = Array.isArray(propVal) ? propVal : [propVal]
            const firstInArray = arrayVal.find(Boolean)
            if (firstInArray && typeof firstInArray === 'object') {
              const childContext = this.getSelectionContext(propName)
              newVal = childContext.overlayData(arrayVal, doLink)
            } else {
              const opts = this.getVarOptions(propName)
              const selectedOpts = arrayVal.map(val => opts.find(opt => opt[varDef.optionKeyName] === val))
              newVal = selectedOpts.map(opt => (opt && opt.__value) || opt)
            }
          } else {
            const scalarVal = Array.isArray(propVal) ? propVal[0] : propVal
            if (typeof scalarVal === 'object') {
              const childContext = this.getSelectionContext(propName, scalarVal, MODE_OVERLAY_CHILD)
              newVal = childContext.scope.value
            } else {
              const opts = this.getVarOptions(propName)
              const selectedOpt = opts.find(opt => opt[varDef.optionKeyName] === scalarVal)
              if (selectedOpt) {
                newVal = selectedOpt.__value || selectedOpt
              }
            }
          }
        } else if (varDef.type === 'date' && typeof propVal === 'string') {
          newVal = parseISO(propVal) // necessary when overlaying on top of existing date values
        }
      }
      newObject[propName] = newVal
    }
    return newObject
  }

  hasDeferredMutations () {
    return !!this.deferredMutations
  }

  async saveDeferredMutations () {
    const linkObj = this.getQueryLink()
    if (linkObj) {
      const queryDef = this.getGlobalDef(linkObj.linkExpr)
      if (queryDef) {
        const cachedValue = this.getDataMgr().getCachedValue(queryDef.typeName, linkObj.keyVal, false, false)
        const newValue = unwrap(this.deferredMutations, queryDef.typeName, this.typeDefs, true)
        const mutatedData = await SaveItem(queryDef, queryDef.keyName, this.parentContext, linkObj.instanceId,
          newValue, cachedValue)
        if (typeof mutatedData === 'undefined') {
          // implies SaveItem (--> UpdateCachedValue) may not have detected any mutation/change in the saved value
          console.log('Warning: no change detected in saved data; possible bug?')
          // avoid clearing the answer just because of that possible bug.
        } else {
          this.parentContext.mutateVarData(this.name, mutatedData)
        }
      }
    }
    if (this.deferredMutations) {
      delete this.deferredMutations
    }
    if (this.origValue) {
      delete this.origValue
    }
  }

  async cancelDeferredMutations () {
    const origValue = this.origValue
    if (origValue && this.parentContext) {
      this.parentContext.mutateVarData(this.name, origValue)
    }
    delete this.origValue
    delete this.deferredMutations
  }

  deferMutateChildSelState (name, typeName, pojoValue) {
    // set up a selection context here so we can get its proper initialized value (with proper prototypes, etc.)
    if (!this.childrenSel) {
      this.childrenSel = {}
    }
    let oldValue
    let newChildState = this.childrenSel[name]
    if (newChildState) {
      oldValue = newChildState.scope && newChildState.scope.value
    } else {
      newChildState = this._createObjectState(name, typeName)
    }
    const isMutable = newChildState.mutable
    // force recursive build-out of state machinery for pojoValue
    newChildState.initSource(pojoValue, MODE_REPLACE)
    // now reset source(s) to null... I believe this is so the new value still registers (later) as a change
    const initializedValue = newChildState.source
    newChildState.resetSource()
    // note: newChildState.scope still has old/prior value in it at this point, but this is okay --
    // the old value is accounted for later in refreshScope().

    // ensure child state has its virtuals initialized properly (and recursively)!
    newChildState.initVirtuals()
    // newChildState.initSelections() // can't do this here because the scope is not updated yet
    newChildState.needInitSelections = true // we'll do it later once the scope has been initialized
    this.childrenSel[name] = newChildState // this is redundant (but harmless) if newChildState already existed above
    if (isMutable && oldValue && !this.updateInProgress()) {
      // we need to stop the current mutation from bubbling up -- because it's a selection OPTION that is being
      // mutated, not the actual answer set -- and instead short-circuit to fast-track a re-render for the
      // appropriate child data state.
      newChildState.deferredMutations = initializedValue
      if (!newChildState.origValue) {
        newChildState.origValue = oldValue
      }
      // this.global.notifyShortCircuit(newChildState.instanceId)
      // return oldValue // returning the old value stops the mutation from bubbling further up the data state
    }
    return initializedValue
  }

  // mutations of the data state work like this:
  // UI component onchange -> parent UI component onchange -> etc. -> "handleObjectChange" of ChildObject, List
  // or AppContainer.  "handleObjectChange" then calls into the apropriate instance of "mutateVarData" with the change.
  // That, in turn, calls this.parentContext.mutateVarData, and so on up to the top of the data state. Then
  // the dataState calls out to the UI (see this.notify) and tells is the data state has been updated.  That
  // handler out in the UI then sets the new data state, which triggers a re-render from the top down.
  //
  // In other words: UI components hoist state changes until it gets to the "we have a new value
  // for some property of an object" level. At that point, the UI calls mutateVarData on the appropriate ObjectState,
  // which handles propagating the change from there.  It could just as well be called "mutateDataProperty".

  mutateVarData (name, value) {
    const existingObject = this.scope && this.scope.value
    const varDef = (this.typeDef.variables || this.typeDef.properties).find(def => def.name === name)
    if (varDef) {
      if (typeof value === 'string' && varDef.type !== 'text' && varDef.type !== 'selection') {
        // avoid putting invalid data into runtime: coerce text-based dates, numbers or Booleans into appropriate type
        value = CoerceScalar(value, varDef, true)
      } else if (typeof value === 'object' && value && varDef.type === 'selection' && varDef.cacheOpts) {
        if (varDef.isList) {
          // should be nothing to do... value should already have been initialized in addNewObject (in arraystate.js)
          // TODO: Refactor so both code paths work the same way!
        } else {
          value = this.deferMutateChildSelState(name, varDef.typeName, value)
        }
      }
    }
    let newObject
    if (existingObject) {
      // get current property to check for change
      const currentVal = existingObject[name]
      if ((currentVal && (value === null)) || (currentVal !== value)) {
        // since we are modifying existing object, we must create a copy of it so it's a new value
        newObject = this._getTypedObject(existingObject, true, true)
        if (value === null) {
          delete newObject[name]
        } else {
          newObject[name] = value
        }
      }
    } else { // no existing object
      if (value !== null && value !== '') {
        newObject = this._getTypedObject(null, false)
        newObject[name] = value
      }
    }
    if (newObject) {
      if (this.notify) {
        // tell the top-level AppContainer react component that it needs to re-render
        this.notify(newObject, false)
      }
      // pass mutation up to parent context
      if (this.parentContext) {
        this.parentContext.mutateVarData(this.name, newObject)
      }
    }
    // does not change data state right away, but returns modified object in a "new data object" member,
    // flagging itself as dirty in the process
    // once the change propagates to the top level (out in the AppContainer react component), that does a setState,
    // as well as calling initSource with the new object to refresh everything.
  }

  mutateListVarData (name, index, value) {
    // this should only be called for "primitive" list variables (i.e., text lists), not object lists.
    // Object lists must be mutated through ArrayState.mutateVarData!
    let existingArray = this.scope && this.scope.value && this.scope.getProperty(name, this.scopeGetter)
    if (existingArray && existingArray.__yobj) {
      existingArray = existingArray.__value
    }
    // create new array value with modified element
    const newArray = existingArray ? existingArray.slice(0, index) : []
    newArray.push(value)
    if (existingArray) {
      newArray.push(...existingArray.slice(index + 1))
    }
    this.mutateVarData(name, newArray)
  }

  deleteListVarData (name, index) {
    // this should only be called for "primitive" list variables (i.e., text lists), not object lists.
    // Object lists must be mutated through ArrayState.deleteObject!
    let existingArray = this.scope && this.scope.value && this.scope.getProperty(name, this.scopeGetter)
    if (existingArray.__yobj) {
      existingArray = existingArray.__value
    }
    if (index === 0 && existingArray.length === 1) {
      this.mutateVarData(name, null)
    } else {
      // create new array value minus the element to delete
      const newArray = existingArray.slice(0, index)
      newArray.push(...existingArray.slice(index + 1))
      this.mutateVarData(name, newArray)
    }
  }

  moveListVarData (name, index, newIndex) {
    // this should only be called for "primitive" list variables (i.e., text lists), not object lists.
    // Object lists must be mutated through ArrayState.moveObject!
    if (index === newIndex) return // no-op
    let existingArray = this.scope && this.scope.value && this.scope.getProperty(name, this.scopeGetter)
    if (existingArray.__yobj) {
      existingArray = existingArray.__value
    }
    // create new array value minus the element to move
    const newArray = existingArray.slice(0, index)
    newArray.push(...existingArray.slice(index + 1))
    // re-insert the old element at the new location
    newArray.splice(newIndex, 0, existingArray[index])
    this.mutateVarData(name, newArray)
  }

  static initializeRuntime (appDef, recordId, isGlobalList, handleRunQuery) {
    const isGlobal = !objectId.isValid(recordId)
    if (isGlobalList && !appDef.types.some(t => t.name === '_top_var')) {
      // dynamically modify appdef to make it work as a "list app"
      addTopLevelShimsToAppDef(appDef)
    }
    const {
      typeDict,
      constDict,
    } = InitAppState(appDef)
    const globalContext = new ObjectState(GLOBALS, GLOBALTYPE, null, typeDict)
    globalContext.initSource(constDict)
    globalContext.isGlobalApp = isGlobal
    globalContext.isGlobalList = isGlobalList
    globalContext.hasDocs = !isGlobal && appDef.generateDocs && (!appDef.extAccess || appDef.showDocs)
    globalContext.appKey = appDef.ksvid
    globalContext.recordId = recordId
    globalContext.linkedQuery = appDef.linkedQuery
    // TODO: when we load existing data into the app runtime, we need to figure out
    // how (when recursively descending to initialize the runtime for that data) to
    // properly initialize (context).linkedKeyName and (context).linkedKeyVal for each object.
    globalContext.initQueries(handleRunQuery)
    globalContext.initVirtuals() // must be called because it's recursive
    globalContext.initScope()
    globalContext.initSelections() // needed if global objects have selection variables!
    return globalContext
  }
}

// const isDef = val => val !== null && typeof val !== 'undefined'

function getFormulaExpr (formulaDef) {
  if (!formulaDef.isList) {
    return formulaDef.expression
  } // else isList
  const exprArray = JSON.parse(formulaDef.expression)
  if (!Array.isArray(exprArray)) {
    throw new Error('Error: unexpected definition for list formula')
  }
  return '[].concat((' + exprArray.join('),(') + '))|filter:this'
}

function getVirtName (varName, virtualName) {
  return varName + '.' + virtualName // '_' + varName + '_' + virtualName
}

function buildLimitFields (instanceId, dependentLinks) {
  if (instanceId && dependentLinks && dependentLinks.length) {
    const result = {}
    // for each dependentLink, add appropriate properties to the result
    for (const dependentLink of dependentLinks) {
      if (dependentLink.instanceId.startsWith(instanceId)) { // sanity check -- expect always to be true
        const diffIdParts = dependentLink.instanceId.slice(instanceId.length + 1).split('.')
        const lastPart = diffIdParts.pop()
        let currObj = result
        for (const interimPart of diffIdParts) {
          currObj = currObj[interimPart] || (currObj[interimPart] = {})
        }
        currObj[lastPart] = 1
      }
    }
    return result
  }
}

function GetFieldMask (typeName, typeDefs, includeInternal = false, recurse = {}) {
  const model = typeDefs[typeName]
  if (typeName in recurse) {
    if (recurse[typeName]) {
      return model.keyType === 'none' ? undefined : 1 // shorten field lists; used to be: { [model.key || 'id$']: 1 }
    } // else allow only one more recursion
    recurse[typeName] = true // flag to end recursion next time
  } // else
  let ignoreVar2
  if (!includeInternal && !model.isExternal && !('f' in model)) {
    // backup for older app definitions that don't include external flags -- primarily old apps integrating with Clio
    if (typeName.includes(':')) {
      ignoreVar2 = 'id$' // at a minimum, ignore id$... but we don't know what ELSE we should be ignoring...
    }
  }
  let vars = model.variables
  if (!includeInternal && model.isExternal) {
    vars = vars.filter(v => v.isExternal)
  }
  vars = vars.filter(v => v.name !== '_link' && v.name !== ignoreVar2)
  const result = {}
  vars.forEach(v => {
    if (!IsObjectVar(v)) {
      result[v.name] = 1
    } else {
      const newRecurse = (v.typeName in recurse)
        ? recurse
        : { ...recurse, [v.typeName]: false }
      const subObj = GetFieldMask(v.typeName, typeDefs, includeInternal, newRecurse)
      if (subObj) {
        result[v.name] = subObj
      }
    }
  })
  return result
}

function undefineOldProperties (newObject, oldObject) {
  if (!newObject || typeof newObject !== 'object') return
  if (!oldObject || typeof oldObject !== 'object') return
  // for each property in oldObject...
  for (const propName of Object.keys(oldObject)) {
    // if that property IS ALREADY present in newObject
    if (propName in newObject) {
      // recurse into that object
      undefineOldProperties(newObject[propName], oldObject[propName])
    } else { // (if that property is NOT already present in newObject)
      // explicitly set the corresponding property in newObject to undefined
      newObject[propName] = undefined
    }
  }
}
